import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PurseService } from '../services';
import {
  cashlessPaymentAction,
  cashlessPaymentErrorAction,
  cashlessPaymentSuccessAction,
  getPursesAction,
  getPursesErrorAction,
  getPursesSuccessAction,
  SetPurseTopupOptionsAction,
  SetPurseTopupOptionsErrorAction,
  SetPurseTopupOptionsSuccessAction,
} from './purses.actions';
import { PAYMENT_SUCCESS } from '@symphony-solo/shared/utils/src/lib';
import { NavController } from '@ionic/angular';
import { PursesFacade } from './purses.facade';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src/lib/+state/authentication.facade';
// import { OrderFacade } from '@symphony-solo/domain/order/src';

@Injectable()
export class PursesEffects {
  constructor(
    private action$: Actions,
    private purseService: PurseService,
    private navCtrl: NavController,
    private pursesFacade: PursesFacade,
    private authenticationFacade: AuthenticationFacade
  ) // private orderFacade: OrderFacade
  {}
  protected getPurses$ = createEffect(() =>
    this.action$.pipe(
      ofType(getPursesAction),
      withLatestFrom(this.authenticationFacade.isAuthenticated$),
      switchMap(([action, isAuthenticated]) => {
        if (isAuthenticated) {
          return this.purseService.getPurses().pipe(
            map((purses) => {
              return getPursesSuccessAction({ purses: purses });
            }),
            catchError(() => {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode('Error on getting purses', ApiResponseCode.Server_Error))
                .build();
              return of(getPursesErrorAction({ failure }));
            })
          );
        } else if (!isAuthenticated) {
          return of(
            getPursesSuccessAction({
              purses: [
                {
                  purseID: 9,
                  purseName: 'Pay by Card',
                  balance: 0,
                  purseType: 'HiddenCreditPurse',
                  defaultPaymentCardID: -1,
                  topUpAllowed: true,
                  autoTopUpAllowed: false,
                  triggerBalance: 10,
                  restoreBalance: 50,
                },
              ],
            })
          );
        } else {
          return of(
            getPursesSuccessAction({
              purses: [],
            })
          );
        }
      })
    )
  );

  protected setTopupOptions$ = createEffect(() =>
    this.action$.pipe(
      ofType(SetPurseTopupOptionsAction),
      switchMap((setPurseTopupOptions) => {
        return this.purseService.setTopupOption(setPurseTopupOptions).pipe(
          tap(() => this.pursesFacade.getPurses()),
          map(() => SetPurseTopupOptionsSuccessAction()),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on setting Purse topup options', ApiResponseCode.Server_Error))
              .build();
            return of(SetPurseTopupOptionsErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected setCashlessPayment$ = createEffect(() =>
    this.action$.pipe(
      ofType(cashlessPaymentAction),
      switchMap(({ cashlessPayment }) => {
        return this.purseService.setCashlessPayment(cashlessPayment).pipe(
          tap((cashlessResponse) => {
            if (cashlessResponse.orderID) {
              // this.orderFacade.setOrderCompletedOn(new Date().toISOString());
              this.navCtrl.navigateForward(`${PAYMENT_SUCCESS}?orderId=${cashlessResponse.orderID}`);
            }
          }),
          map((cashlessResponse) => cashlessPaymentSuccessAction({ cashlessResponse })),
          catchError((err) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(
                new AppStateFailureItemWithErrorCode(err?.error ? err?.error : 'Error on cashless payment', ApiResponseCode.Server_Error)
              )
              .build();
            return of(cashlessPaymentErrorAction({ failure }));
          })
        );
      })
    )
  );
}
